import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Props {
  value: number | null;
  onSave: (next: number | null) => Promise<void>;
  editable: boolean;
  min?: number;
  allowDecimal?: boolean;
  allowNull?: boolean;
  placeholder?: string;
  suffix?: string;
}

const fmt = (v: number | null) =>
  v == null ? '—' : new Intl.NumberFormat('vi-VN').format(v);

export default function InlineNumberCell({
  value,
  onSave,
  editable,
  min = 0,
  allowDecimal = true,
  allowNull = true,
  placeholder = '—',
  suffix = '',
}: Props) {
  const [mode, setMode] = useState<'read' | 'edit' | 'saving'>('read');
  const [draft, setDraft] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === 'edit') {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [mode]);

  const enterEdit = (e: React.MouseEvent) => {
    if (!editable || mode !== 'read') return;
    e.stopPropagation();
    setDraft(value == null ? '' : String(value));
    setMode('edit');
  };

  const commit = async () => {
    const raw = draft.trim();
    let next: number | null;
    if (raw === '') {
      if (!allowNull) {
        toast.error('Giá trị không được để trống');
        setMode('read');
        return;
      }
      next = null;
    } else {
      const n = allowDecimal ? Number(raw.replace(',', '.')) : parseInt(raw, 10);
      if (!Number.isFinite(n) || n < min) {
        toast.error(`Giá trị phải ≥ ${min}`);
        setMode('read');
        return;
      }
      next = allowDecimal ? n : Math.trunc(n);
    }
    if (next === value) {
      setMode('read');
      return;
    }
    setMode('saving');
    try {
      await onSave(next);
    } catch {
      // mutation handles toast
    } finally {
      setMode('read');
    }
  };

  if (mode === 'saving') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>{fmt(value)}{suffix}</span>
      </div>
    );
  }

  if (mode === 'edit') {
    return (
      <Input
        ref={inputRef}
        type="number"
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        step={allowDecimal ? 'any' : 1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setMode('read');
          }
        }}
        className="h-8 w-24 text-sm"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={enterEdit}
      className={cn(
        'text-sm text-left w-full px-2 py-1 rounded',
        editable
          ? 'cursor-pointer hover:bg-muted/60'
          : 'cursor-default text-muted-foreground',
      )}
      disabled={!editable}
    >
      {value == null ? placeholder : fmt(value)}{suffix}
    </button>
  );
}
